<template>
  <div>
    <div class="accordion-header" :class="{ 'expanded': isExpanded }" @click="toggleAccordion">
      <div class="d-flex align-items-center justify-content-between w-100">

        <div class="honoraire-name">
          <div class="bold-text honoraire-title">{{ operationHonoraire.type }}</div>
          <div v-if="isGeneral" class="grey-text lots-info">{{ `${formatLots}` }}</div>
        </div>

        <div class="honoraire-info devis-factures-info">
          <span class="">{{ getDevisCount() }}/{{ operationHonoraire.devis.length + ' ' + $t('devis') }}  - {{ getFacturesCount() + ' ' + $t('factures') }} </span>
        </div>

        <div class="honoraire-info amounts-info">
          <span class="info-strong-text bold-text total-amount">{{ formatEuro(prixAffiche) }}</span>

          <div class="progress-container">
            <div class="progress-bar" :style="{ width: getProgressPercentage() + '%' }"></div>
          </div>

          <span class="info-strong-text remaining-amount">{{ formatEuro(remainingAffiche) }}</span>
        </div>


        <div class="d-flex align-items-center">
          <pencil-icon class="action-icon edit-icon" size="20" @click.stop="editOperationHonoraire"/>
          <trash-icon class="action-icon delete-icon" size="20" @click.stop="removeOperationHonoraire"/>
          <div class="accordion-arrow ml-2">
            <chevron-down-icon v-if="!isExpanded" size="16" class="arrow-icon"/>
            <chevron-up-icon v-else size="16" class="arrow-icon"/>
          </div>
        </div>
      </div>
    </div>

    <div v-show="isExpanded" class="accordion-content">
      <div class="row mt-3">
        <div class="col-md-12 details-section">
          <template v-if="operationHonoraire.hasOwnProperty('factures') && operationHonoraire.factures && operationHonoraire.factures.length > 0">
            <span class="details-text"> {{ $t('Details') + operationHonoraire.type }} : {{ $t('of') + formatDate(operationHonoraire.date) + ' ' + $t('to') + formatDate(operationHonoraire.date)}} </span>
            <div>
              <alert-circle-icon class="main-red-color" size="22"/>
              <span class="main-red-color">{{ $t('devis.facture.already.linked') }}</span>
            </div>
          </template>
          <template v-else>
            <span class="details-text"> {{ $t('Details') + operationHonoraire.type }} : {{ $t('of') + formatDate(operationHonoraire.date) + ' ' + $t('to') + formatDate(operationHonoraire.date)}} </span>
            <span class="bold-text addLink" @click="addDevis"> + {{ $t('Add') }} {{ $t('devis') }}</span>
          </template>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-md-12">
          <b122-table
            v-if="operationHonoraire && operationHonoraire.id"
            v-show="tableTotalRows > 0"
            :key="'B122TableFactures-' + operationHonoraire.id + '-' + tableRefreshKey"
            ref="B122TableFactures"
            :empty-text="'Aucune facture ou devis associé pour le moment'"
            :empty-title="'Aucune facture ou devis'"
            :fields="tableFieldsFactures"
            :get-b122-table-results-url="getFacturesTableUrl"
            :identifier="'B122TableFactures-' + operationHonoraire.id"
            :use-filters="false"
            :use-pagination="false"
            :create-item="false"
            init-b122-table-url="/operationHonoraire/initFacturesB122Table"
            @setTotalCount="tableTotalRows = $event"
            @clickOnShowBtn="clickOnShowFactureBtn"
            @clickOnEditBtn="clickOnEditFactureBtn"
            @clickOnDeleteBtn="clickOnDeleteFactureBtn"
            @tableRefreshed="onTableRefreshed">

            <template #cell(facture.devis.montant)="data">
              <template v-if="data.item.facture.devis && (data.item.facture.devis.montant || data.item.facture.devis.montant === 0)">
                {{ formatEuroFromCentimes(data.item.facture.devis.montant, data.item.facture.devis.tauxTVA) }}
              </template>
              <template v-else>
                <span class="text-muted">-</span>
              </template>
            </template>

            <template #cell(facture.devis.numero)="data">
              <div v-if="data.item.facture.devis && data.item.facture.devis.numero && data.item.facture.devis.numero !== '-'">

                <div v-if="data.item.facture.devis.documents && data.item.facture.devis.documents.length > 0">
                  <b-link class="light-blue-color underline-text" v-on:click="getDocument(data.item.facture.devis.documents[0].fileUrl)">
                    {{ truncateText(data.item.facture.devis.documents[0].fileName, 30) }}
                  </b-link>
                </div>

                <div class="devis-number-grey d-flex align-items-center">
                  <span>{{ 'N° ' + data.item.facture.devis.numero }}</span>
                  <span v-if="data.item.facture.devis.status === 'VALIDATED'" class="ml-2 text-success">✓</span>
                  <span v-else-if="data.item.facture.devis.status === 'REFUSED'" class="ml-2 text-danger">✗</span>
                </div>
              </div>
              <div v-else>
                <span class="text-muted">-</span>
              </div>
            </template>

            <template #cell(facture.statusObj)="data">
              <div v-if="data.item.facture.isDevisOnly && data.item.facture.devis && data.item.facture.devis.status === 'VALIDATED'">
                  <span class="status-a-payer status-badge-small">
                    {{ $t('status.aPayer') }} - {{ formatEuroFromCentimes(data.item.facture.prixTTC, data.item.facture.tauxTVA) }}
                  </span>
              </div>
              <div v-else-if="data.item.facture.isDevisOnly">
                <span class="text-muted">-</span>
              </div>
              <div v-else>
                  <span :class="getStatusClass(data.item.facture.statusObj.text)" class="status-badge-small">
                    {{ getStatusText(data.item.facture.statusObj.text, [data.item.facture]) }} - {{ formatEuroFromCentimes(data.item.facture.prixTTC, data.item.facture.tauxTVA) }}
                  </span>
              </div>
            </template>

            <template #cell(documents)="data">
              <div v-if="data.item.facture.isDevisOnly">
                <span class="text-muted">-</span>
              </div>

              <div v-else>
                <div class="font-weight-medium">
                  {{ data.item.facture.libelle || data.item.facture.nom || 'N/A' }}
                </div>
                <div v-for="document in data.item.documents" :key="document.id" class="mt-1">
                  <b-link class="light-blue-color underline-text" style="font-size: 0.85rem;" @click="getDocument(document.fileUrl)">
                    {{ truncateText(document.fileName, 25) }}
                  </b-link>
                </div>
              </div>
            </template>
          </b122-table>
          <p v-if="operationHonoraire && operationHonoraire.id" v-show="tableTotalRows === 0" class="text-muted text-center">Aucune facture ou devis associé pour le moment</p>
        </div>
      </div>
    </div>

    <index-devis-honoraire-modal
      :key="this.modalKey + '-add'"
      :ref="'indexDevisModal-' + this.operationHonoraire.id"
      :operation-honoraire="this.operationHonoraire"
      :operation-honoraire-name="this.operationHonoraire.type"
      @didAddDevis="addDevis"
      @didEditDevis="editDevis"/>

    <add-devis-honoraire-modal
      :key="this.modalKey + '-edit'"
      :ref="'addDevisModal-' + this.operationHonoraire.id"
      :operation-honoraire="this.operationHonoraire"
      :operation-honoraire-name="this.operationHonoraire.type"
      :selected-devis-index-prop="this.selectedDevisIndex"
      :is-h-t="isHT"
      :tauxTVAList="tauxTVAList"
      @didAddDevis="didAddDevis"/>

    <add-facture-honoraire-modal
      :key="this.operationHonoraire.lastModifDate + '-editFacture'"
      :ref="'addFactureModal-' + this.operationHonoraire.id"
      :operation-honoraire="this.operationHonoraire"
      :operation-honoraire-name="this.operationHonoraire.type"
      :statusFactureList="statusFactureList"
      @didAddFacture="didAddFacture"/>

    <!-- Modal de choix d'édition -->
    <b-modal
      :id="'edit-choice-modal-honoraire-' + operationHonoraire.id"
      :title="$t('Modify.poste')"
      centered
      hide-footer
      @hidden="hideEditModal">

      <div>
        <p class="mb-4">{{ $t('Que souhaitez-vous modifier ?') }}</p>

        <div class="row">
          <div class="col-md-6">
            <b-button
              variant="primary"
              size="lg"
              block
              class="mb-3"
              @click="editDevisFromModal">
              <i class="fas fa-file-invoice mr-2"></i>
              {{ $t('Modifier') }} {{ $t('devis') }}
            </b-button>
          </div>
          <div class="col-md-6">
            <b-button
              variant="primary"
              size="lg"
              block
              class="mb-3"
              @click="editFactureFromModal">
              <i class="fas fa-receipt mr-2"></i>
              {{ $t('Modifier') }} {{ $t('facture') }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Modal de confirmation de suppression -->
    <b-modal
      :id="'delete-confirmation-modal-honoraire-' + operationHonoraire.id"
      title="Confirmation de suppression"
      centered
      @ok="confirmDeleteFacture"
      @cancel="cancelDeleteFacture"
      ok-title="Supprimer"
      cancel-title="Annuler"
      ok-variant="danger"
      cancel-variant="primary">

      <div v-if="selectedItemForDelete">
        <p class="mb-3">
          {{ $t('Êtes-vous sûr de vouloir supprimer cet élément ?') }}
        </p>
        <div class="alert alert-warning">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <strong>{{ $t('Attention') }}</strong> {{ $t('Cette action est irréversible.') }}
          <template v-if="selectedItemForDelete.isDevisOnly">
            <br><br>
            <span>{{ $t('La suppression du devis entraînera :') }}</span>
            <ul class="mb-0 mt-2">
              <li>{{ $t('Suppression du devis et de son échéancier') }}</li>
              <li>{{ $t('Suppression de toutes les factures associées') }}</li>
              <li>{{ $t('Les honoraires seront conservés') }}</li>
            </ul>
          </template>
          <template v-else>
            <br><br>
            <span>{{ $t('La suppression de la facture entraînera :') }}</span>
            <ul class="mb-0 mt-2">
              <li>{{ $t('Suppression définitive de la facture') }}</li>
              <li>{{ $t('Le devis et les honoraires seront conservés') }}</li>
            </ul>
          </template>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>

import AutocompleteField from "../../../components/b122/editFields/AutocompleteField";
import api from "../../../services/api";
import _ from "lodash";
import AddDevisHonoraireModal from "./AddDevisHonoraireModal";
import IndexDevisHonoraireModal from "./IndexDevisHonoraireModal";
import AddFactureHonoraireModal from "./AddFactureHonoraireModal";
import B122Table from "../../../components/b122/B122Table";

export default {

  name: "OperationHonoraireComponent",

  components: {
    AutocompleteField,
    AddDevisHonoraireModal,
    IndexDevisHonoraireModal,
    AddFactureHonoraireModal,
    B122Table
  },

  props: {
    operationHonoraire: {
      type: Object,
      default: null
    },
    contactNames: {
      type: Array,
      default: () => [],
    },
    isGeneral: {
      type: Boolean,
      default: false
    },
    index: null,

    isCreateBilan: {
      type: Boolean,
      default: false
    },
    operationLots: {
      type: Array,
      default() {
        return []
      }
    },
    selectedLots: {
      type: Array,
      default() {
        return []
      }
    },
    isHT: {
      type: Boolean,
      default: false
    }

  },

  data() {
    return {
      prixTTC: 0,
      devisValidated: null,
      factureValidated: null,
      modalKey: 0,
      selectedDevisIndex: null,
      statusFactureList: [],
      tauxTVAList: [],
      isExpanded: false,
      selectedItemForEdit: null,
      selectedItemForDelete: null,
      tableFieldsFactures: [
        {
          label: this.$t('See'),
          key: 'show',
          sortable: false,
        },
        {
          label: 'Nom',
          key: 'facture.nom',
          sortable: true,
        },
        {
          label: 'Artisan',
          key: 'facture.artisan',
          sortable: true,
        },
        {
          label: 'Devis',
          key: 'facture.devis.numero',
          sortable: false,
        },
        {
          label: 'Montant du devis',
          key: 'facture.devis.montant',
          sortable: true,
        },
        {
          label: 'Facture',
          key: 'documents',
          sortable: false,
        },
        {
          label: 'Statut',
          key: 'facture.statusObj',
          sortable: true,
        },
        {
          label: '',
          key: 'actions',
          sortable: false,
        }
      ]
      ,
      tableTotalRows: 0,
      tableRefreshKey: 0
    }
  },

  emits: ['removedOperationHonoraire', 'didChangeValue', 'didAddDevis', 'editedOperationHonoraire'],

  async mounted() {
    await this.getTauxTVA()
    await this.getStatusFacture()
    await this.manageDevisValidated()
    this.manageFactureValidated()
    await this.computeTotals()
  },

  filters: {
    formatDateReverse: function (value) {
      if (!value) return ''
      return new Date(value).toLocaleDateString()
    },

    formatEuro(amount) {
      if (!amount && amount !== 0) return '0 €';

      const formatter = new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
      });
      return formatter.format(amount);
    },
  },

  computed: {
    prixAffiche() {
      const oh = this.operationHonoraire
      if (!oh) return 0

      if (this.isHT) {
        if (oh.prixHT !== undefined && oh.prixHT !== null) {
          return Number(oh.prixHT)
        } else if (oh.prixTTC) {
          return this.convertTTCtoHT(oh.prixTTC, oh.tauxTVA)
        }
      }
      return Number(oh.prixTTC) || 0
    },

    remainingAffiche() {
      const oh = this.operationHonoraire
      if (!oh) return 0

      if (this.isHT) {
        if (oh.remainingHT !== undefined && oh.remainingHT !== null) {
          return Number(oh.remainingHT)
        } else if (oh.remainingTTC) {
          return this.convertTTCtoHT(oh.remainingTTC, oh.tauxTVA)
        }
      }
      return Number(oh.remainingTTC) || 0
    },
    formatLots() {
      if (this.operationLots.length === this.selectedLots.length) {
        return 'tous les lots';
      } else {
        return this.selectedLots.sort((a, b) => a - b).map(lotId => {
          let lot = this.operationLots.find(lotReal => lotReal.id === lotId);
          if (lot) {
            return lot.lotCoPro ? `Lot ${lot.lotCoPro}` : `Lot ${lot.lotNumber}`;
          }
        }).join(', ');
      }
    },
    getFacturesTableUrl() {
      const url = `/operationHonoraire/getFacturesB122TableResults?restrictbylong-honoraireId=${this.operationHonoraire.id}`;
      return url;
    }
  },

  watch: {
    operationHonoraire: {
      handler: async function () {

        await this.manageDevisValidated()
        this.manageFactureValidated()
        await this.computeTotals()
      },
      deep: true
    }
  },

  methods: {
    convertTTCtoHT(montant, tauxTVA) {
      if (!montant || !tauxTVA) return montant
      const taux = parseFloat(tauxTVA.replace('%', '')) / 100
      return montant / (1 + taux)
    },
    toggleAccordion() {
      this.isExpanded = !this.isExpanded;
    },

    formatEuroFromCentimes(centimes, tauxTVA = null) {
      if (!centimes && centimes !== 0) return '0 €';

      let euros = centimes / 100;
      if (this.isHT && tauxTVA) {
        const taux = parseFloat(tauxTVA.replace('%', '')) / 100;
        euros = euros / (1 + taux);
      }
      const formatter = new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
      });
      return formatter.format(euros);
    },

    getDevisCount() {
      if (!this.operationHonoraire.devis) return 0;
      return this.operationHonoraire.devis.filter(devis => devis.validated).length;
    },

    getFacturesCount() {
      let count = 0;

      if (this.operationHonoraire.factures && this.operationHonoraire.factures.length > 0) {
        count += this.operationHonoraire.factures.length;
      }

      if (this.operationHonoraire.devis) {
        this.operationHonoraire.devis.forEach(devis => {
          if (devis.schedule && devis.schedule.paiements) {
            devis.schedule.paiements.forEach(paiement => {
              if (paiement.facture) {
                count++;
              }
            });
          }
        });
      }

      return count;
    },


    hasFacturesOrDevis() {
      if (this.getFacturesCount() > 0) {
        return true;
      }

      if (this.operationHonoraire.devis && this.operationHonoraire.devis.length > 0) {
        return true;
      }

      return false;
    },


    formatEuro(amount) {
      if (!amount && amount !== 0) return '0 €';

      const formatter = new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
      });
      return formatter.format(amount);
    },

    getProgressPercentage() {
      const total = this.prixAffiche || 0
      const restantAPayer = this.remainingAffiche
      const paye = total - restantAPayer
      if (total === 0) return 0
      return Math.min(100, Math.max(0, (paye / total) * 100))
    },

    getRestantAPayer() {
      if (this.remainingTTC !== undefined && this.remainingTTC !== null) {
        return this.remainingTTC
      }
      return this.operationHonoraire.rap !== undefined ? this.operationHonoraire.rap : this.prixTTC;
    },

    resetSelectedDevisIndex() {
      this.selectedDevisIndex = null;
    },

    async getTauxTVA() {
      await api
        .get(`/operation/getTauxTVA`)
        .then(response => {
          this.tauxTVAList = response.data
        })
    },

    async getStatusFacture() {
      await api
        .get(`/facturePayment/getStatusFacture`)
        .then(response => {
          this.statusFactureList = response.data
        })
    },

    removeOperationHonoraire() {
      const honoraireDetails = this.operationHonoraire;
      let detailsMessage = '';
      let countFacture = 0
      let hasDevis = false;

      if (honoraireDetails.devis && honoraireDetails.devis.length > 0) {
        detailsMessage += `${honoraireDetails.devis.length} devis`;
        hasDevis = true;
      }

      if (honoraireDetails.facture) {
        countFacture++
      }

      honoraireDetails.devis.forEach(devis => {
        if(devis.hasOwnProperty('schedule')&& !!devis.schedule.id){
          if(devis.schedule.hasOwnProperty("paiements") && devis.schedule.paiements.length > 0){
            devis.schedule.paiements.forEach(paiement => {
              if(paiement.facture) {
                countFacture++
              }
            })
          }
        }
      })
      if(countFacture > 0){
        detailsMessage += ` ${honoraireDetails.devis && honoraireDetails.devis.length > 0 ? 'et ':''} ${countFacture} facture(s)`;
      }

      let fullMessage
      if(!hasDevis && countFacture  === 0){
        fullMessage = this.$i18n.t('operationHonoraire.confirm.delete').toString()
      }
      else {
        fullMessage = this.$i18n.t('operationHonoraire.confirm.delete.with.details',{details:detailsMessage}).toString()
      }

      this.$confirm(fullMessage).then(() => {
        this.$emit('removedOperationHonoraire', this.operationHonoraire);
      });
    },

    editOperationHonoraire() {
      this.$emit('editedOperationHonoraire', this.operationHonoraire)
    },

    debounceEmitChangeValue: _.debounce(function () {
      this.emitChangeValue()
    }, 500),

    async emitChangeValue() {
      await this.computeTotals()

    },


    addDevis() {
      this.selectedDevisIndex = null
      if(!this.operationHonoraire.contactName || this.operationHonoraire.contactName == ''){
        this.$alert(this.$i18n.t('honoraire.cant.add.devis').toString()).then(() => {
        });
      }
      else {
        this.$bvModal.show('add-devis-modal-' + this.operationHonoraire.id)
      }
    },

    addFacture() {
      this.$bvModal.show('add-facture-modal-' + this.operationHonoraire.id)
    },


    indexDevis() {
      this.$bvModal.show('index-devis-modal-' + this.operationHonoraire.id)
    },

    editDevis(index) {
      this.selectedDevisIndex = index
      this.modalKey += 1
      setTimeout(() => this.$bvModal.show('add-devis-modal-' + this.operationHonoraire.id), 100);
    },

    getSelectedValue(data) {
      this.operationHonoraire.contactName = data
      this.emitChangeValue()
    },


    async didAddDevis(operationHonoraire) {
      this.selectedDevisIndex = null
      this.operationHonoraire = operationHonoraire
      await this.manageDevisValidated()
      await this.computeTotals()
      this.modalKey += 1
      this.$emit('didChangeValue', this.operationHonoraire)
    },

    async manageDevisValidated() {
      let devis = this.operationHonoraire.devis
      this.devisValidated = null

      if (devis) {
        devis.forEach((item, index) => {
          if (item.validated) {
            this.devisValidated = item
          }
        })
      }

      if (!this.devisValidated && this.operationHonoraire.isPourcentageConstructionMode && this.operationHonoraire.pourcentage && this.operationHonoraire.pourcentage > 0) {
        const baseTotal = await this.getBaseTotalHonoraire()
        this.prixTTC = baseTotal && !isNaN(baseTotal) ? Number(baseTotal).toFixed(2) : 0
      }
    },

    manageFactureValidated() {
      let factures = this.operationHonoraire.factures
      this.factureValidated = null

    },

    async didAddFacture(operationHonoraire) {
      this.operationHonoraire = operationHonoraire
      this.manageFactureValidated()
      await this.computeTotals()

      this.$emit('didChangeValue', this.operationHonoraire)
    },

    clickOnShowFactureBtn(row) {

      if (row.item.facture.isDevisOnly && row.item.facture.devisId) {
        this.$router.push({
          name: 'OperationDevisFactureShow',
          params: {
            operationId: this.operationHonoraire.operation && this.operationHonoraire.operation.hasOwnProperty('id') && this.operationHonoraire.operation.id  ? this.operationHonoraire.operation.id : this.operationHonoraire.operationLot.operation.id,
            devisId: row.item.facture.devisId
          }
        })
        return
      }

      this.$router.push({
        name: 'OperationFactureShow',
        params: {
          factureId: row.item.facture.id,
          operationId: this.operationHonoraire.operation && this.operationHonoraire.operation.hasOwnProperty('id') && this.operationHonoraire.operation.id  ? this.operationHonoraire.operation.id : this.operationHonoraire.operationLot.operation.id
        }
      })
    },

    truncateText(text, maxLength) {
      if (text && text.length > maxLength) {
        return text.substring(0, maxLength - 3) + '...';
      }
      return text;
    },

    getStatusClass(status) {
      switch (status) {
        case 'PAYE':
        case 'PAID':
          return 'status-paye'
        case 'A_PAYER':
        case 'UNPAID':
          return 'status-a-payer'
        case 'RESTE_A_PAYER':
          return 'status-reste-a-payer'
        case 'EN_ATTENTE':
        case 'PENDING':
          return 'status-en-attente'
        default:
          return 'status-default'
      }
    },

    getStatusText(status, factures) {
      switch (status) {
        case 'PAYE':
          const datePaiement = this.getFirstPaidDate(factures)
          return datePaiement ? `Payé le ${datePaiement}` : this.$t('status.paye')
        case 'A_PAYER':
          return this.$t('status.aPayer')
        case 'RESTE_A_PAYER':
          return this.$t('status.resteAPayer')
        case 'EN_ATTENTE':
          return this.$t('status.enAttente')

        case 'PAID':
          const dateFacturePaiement = factures && factures[0] && factures[0].paidDate
          return dateFacturePaiement ? `Payé le ${this.formatDate(dateFacturePaiement)}` : this.$t('status.paye')
        case 'UNPAID':
          return this.$t('status.aPayer')
        case 'PENDING':
          return this.$t('status.enAttente')
        default:
          return status
      }
    },

    getFirstPaidDate(factures) {
      if (!factures || factures.length === 0) return null

      const facturePayee = factures.find(facture =>
        (facture.status === 'PAID' || facture.status === 'PAYE') && facture.paidDate
      )

      if (facturePayee && facturePayee.paidDate) {
        return this.formatDate(facturePayee.paidDate)
      }

      return null
    },

    formatDate(dateValue) {
      if (!dateValue) return null

      try {
        let date
        if (typeof dateValue === 'number' || typeof dateValue === 'string') {

          const timestamp = parseInt(dateValue)
          if (!isNaN(timestamp)) {
            date = new Date(timestamp)
          } else {

            const parts = dateValue.split('-')
            if (parts.length === 3) {
              return `${parts[2]}/${parts[1]}/${parts[0]}`
            }
            return dateValue
          }
        } else {
          date = new Date(dateValue)
        }

        if (isNaN(date.getTime())) {
          return dateValue
        }

        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()

        return `${day}/${month}/${year}`
      } catch (error) {
        console.error('Erreur formatage date:', error)
        return dateValue
      }
    },

    getDocument(fileUrl) {
      let url = process.env.SERVER_URL + fileUrl
      window.open(url, '_blank').focus()
    },

    formatDate(dateValue) {
      if (!dateValue) return 'N/A'

      try {
        if (typeof dateValue === 'string' && dateValue.includes('-')) {
          const parts = dateValue.split('-')
          if (parts.length === 3) {
            return `${parts[2]}/${parts[1]}/${parts[0]}`
          }
        }

        let date
        if (typeof dateValue === 'number' || typeof dateValue === 'string') {
          const timestamp = parseInt(dateValue)
          if (!isNaN(timestamp)) {
            const tsMs = timestamp < 1000000000000 ? timestamp * 1000 : timestamp
            date = new Date(tsMs)
          } else {
            date = new Date(dateValue)
          }
        } else {
          date = new Date(dateValue)
        }

        if (isNaN(date.getTime())) {
          return 'N/A'
        }

        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()

        return `${day}/${month}/${year}`
      } catch (error) {
        console.error('Erreur formatage date:', error)
        return 'N/A'
      }
    },

    clickOnEditFactureBtn(row) {
      const item = row.item.facture

      const hasDevis = item.devis && item.devis.numero && item.devis.numero !== '-'
      const hasFacture = item.id && !item.isDevisOnly

      if (hasDevis && hasFacture) {
        this.selectedItemForEdit = item
        this.$bvModal.show('edit-choice-modal-honoraire-' + this.operationHonoraire.id)
      } else if (hasFacture) {
        this.editFacture(item)
      } else if (hasDevis) {
        this.editDevis(item)
      }
    },

    clickOnDeleteFactureBtn(row) {
      this.selectedItemForDelete = row.item.facture
      this.$bvModal.show('delete-confirmation-modal-honoraire-' + this.operationHonoraire.id)
    },

    editFacture(facture) {
      this.$router.push({
        name: 'OperationFactureEdit',
        params: {
          operationId: this.getOperationId(),
          factureId: facture.id
        },
        query: {
          from: 'OperationHonoraireComponent'
        }
      })
    },

    editDevis(devis) {
      const resolvedDevisId = (devis && devis.devisId)
        || (this.selectedItemForEdit && this.selectedItemForEdit.devisId)


      if (!resolvedDevisId) {
        console.warn('[DEBUG][Honoraire] Missing devisId, abort navigation. devis=', devis, 'selectedItemForEdit=', this.selectedItemForEdit)
        return
      }
      this.$router.push({
        name: 'OperationDevisEdit',
        params: {
          operationDevisId: resolvedDevisId
        },
        query: {
          from: 'OperationHonoraireComponent'
        }
      })
    },

    getOperationId() {
      if (this.operationHonoraire.operation && this.operationHonoraire.operation.hasOwnProperty('id') && this.operationHonoraire.operation.id) {
        return this.operationHonoraire.operation.id
      } else if (this.operationHonoraire.operationLot && this.operationHonoraire.operationLot.operation && this.operationHonoraire.operationLot.operation.id) {
        return this.operationHonoraire.operationLot.operation.id
      }
      return null
    },

    async confirmDeleteFacture() {
      try {

        const item = this.selectedItemForDelete
        let apiUrl = ''

        if (item.isDevisOnly && item.devisId) {
          apiUrl = `/operationDevis/${item.devisId}`
        }

        else if (item.id) {
          apiUrl = `/facturePayment/${item.id}`
        }
        else {
          throw new Error('Impossible de déterminer l\'élément à supprimer')
        }

        const response = await api.delete(apiUrl)

        const factures = await this.getFacturesList()

        this.operationHonoraire.factures = factures || []

        try {
          const devisResponse = await api.get(`/operationHonoraire/getDevis?honoraireId=${this.operationHonoraire.id}`)
          this.operationHonoraire.devis = devisResponse.data || []
        } catch (error) {
          console.error('Erreur lors de la récupération des devis:', error)
          this.operationHonoraire.devis = this.operationHonoraire.devis || []
        }

        this.$bvModal.hide('delete-confirmation-modal-honoraire-' + this.operationHonoraire.id)

        this.tableRefreshKey++

        await this.computeTotals()


        this.$bvToast.toast('Suppression effectuée avec succès', {
          autoHideDelay: 4000,
          toaster: 'b-toaster-bottom-right',
          noCloseButton: true,
          variant: 'success',
          solid: true,
        })

        this.selectedItemForDelete = null

      } catch (error) {
        console.error('[DEBUG][Honoraire] Erreur lors de la suppression:', error)

        this.$bvToast.toast('Erreur lors de la suppression', {
          autoHideDelay: 4000,
          toaster: 'b-toaster-bottom-right',
          noCloseButton: true,
          variant: 'danger',
          solid: true,
        })
      }
    },

    cancelDeleteFacture() {
      this.$bvModal.hide('delete-confirmation-modal-honoraire-' + this.operationHonoraire.id)
      this.selectedItemForDelete = null
    },

    hideEditModal() {
      this.selectedItemForEdit = null
    },

    editDevisFromModal() {
      const cached = this.selectedItemForEdit
      this.$bvModal.hide('edit-choice-modal-honoraire-' + this.operationHonoraire.id)
      setTimeout(() => {
        this.editDevis(cached)
      }, 300)
    },

    editFactureFromModal() {
      const cached = this.selectedItemForEdit
      this.$bvModal.hide('edit-choice-modal-honoraire-' + this.operationHonoraire.id)
      setTimeout(() => {
        this.editFacture(cached)
      }, 300)
    },

    async onTableRefreshed() {

      await this.computeTotals()
    },

    async getBaseTotalHonoraire() {

      if (this.operationHonoraire.isPourcentageConstructionMode && this.operationHonoraire.pourcentage && this.operationHonoraire.pourcentage > 0) {
        try {
          const operation = this.operationHonoraire.operation || (this.operationHonoraire.operationLot && this.operationHonoraire.operationLot.operation);
          if (operation) {
            const response = await api.get(`/operationHonoraire/getCoutTravaux?operationId=${operation.id}`);
            const baseValue = (response.data.coutTravaux || 0) / 100;
            return (baseValue * this.operationHonoraire.pourcentage / 100);
          }
        } catch (error) {
          console.error('Erreur lors du calcul du pourcentage:', error);
        }
      }


      let total = this.operationHonoraire.prixTTC
      total = total && !isNaN(total) ? Number(total) : 0
      return total
    },
    getValidatedDevis() {
      const devis = this.operationHonoraire.devis || []
      return devis.find(d => d && d.validated)
    },
    async getFacturesList() {
      try {
        if (!this.operationHonoraire || !this.operationHonoraire.id) {
          return []
        }
        const response = await api.get(`/operationHonoraire/getFactures?honoraireId=${this.operationHonoraire.id}`)
        return response.data || []
      } catch (error) {
        console.error('Erreur lors de la récupération des factures:', error)
        return []
      }
    },
    sumFactures(factures) {
      return (factures || []).reduce((acc, f) => acc + (Number(f && f.prixTTC) || 0), 0)
    },
    sumFacturesPaid(factures) {
      return (factures || []).reduce((acc, f) => {
        const status = f && f.status
        const isPaid = status === 'PAYE' || status === 'PAID'
        return acc + (isPaid ? (Number(f.prixTTC) || 0) : 0)
      }, 0)
    },
    async computeTotals() {
      const baseTotal = await this.getBaseTotalHonoraire()
      const factures = await this.getFacturesList()
      const validatedDevis = this.getValidatedDevis()


      let total = baseTotal
      let remaining = baseTotal

      if (factures && factures.length > 0) {

        const facturesPayees = this.sumFacturesPaid(factures)
        const totalFactures = this.sumFactures(factures)

        if (validatedDevis) {

          const montantDevis = Number(validatedDevis.prixTTC) || 0
          if (totalFactures > montantDevis) {

            total = totalFactures
            remaining = total - facturesPayees

          } else {

            total = montantDevis
            remaining = total - facturesPayees

          }
        } else {

          total = totalFactures
          remaining = total - facturesPayees

        }


      } else if (validatedDevis && (!factures || factures.length === 0)) {

        total = Number(validatedDevis.prixTTC) || baseTotal
        remaining = total

      } else {

        total = baseTotal
        remaining = total

      }


      this.prixTTC = total && !isNaN(total) ? Number(total).toFixed(2) : 0
      this.remainingTTC = remaining && !isNaN(remaining) ? Number(remaining).toFixed(2) : 0

    }
  }

}

</script>

<style scoped>

.accordion-header {
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 8px;
}

.accordion-header.expanded {
  border-radius: 8px 8px 0 0;
}

.accordion-header:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.accordion-content {
  background-color: white;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 16px;
  margin-top: -8px;
  margin-bottom: 8px;
}

.honoraire-name {
  flex: 0 0 200px;
  text-align: left;
  min-width: 200px;
  max-width: 200px;
}

.honoraire-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lots-info {
  font-size: 0.85rem;
  margin-top: 2px;
}

.honoraire-info {
  flex: 0 0 auto;
  margin: 0 20px;
  display: flex;
  align-items: center;
}

.devis-factures-info {
  flex: 0 0 200px;
  min-width: 200px;
  max-width: 200px;
  justify-content: center;
}

.amounts-info {
  flex: 0 0 250px;
  min-width: 250px;
  max-width: 250px;
  justify-content: center;
}

.total-amount {
  min-width: 80px;
  text-align: right;
}

.remaining-amount {
  min-width: 80px;
  text-align: left;
}

.info-text {
  color: #6c757d;
  font-size: 0.9rem;
  font-weight: 500;
}

.info-strong-text {
  color: var(--main-color);
  font-size: 0.9rem;
}

.progress-container {
  width: 60px;
  height: 8px;
  background-color: #e9ecef;
  border-radius: 900px;
  margin: 0 8px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: var(--main-color-hover);
  border-radius: 900px;
  transition: width 0.3s ease;
}

.amount-text {
  color: #28a745;
  font-size: 0.9rem;
  font-weight: bold;
}

.action-icon {
  margin-right: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.edit-icon {
  color: var(--main-color);
}

.delete-icon {
  color: #9E0430;
}

.accordion-arrow {
  display: flex;
  align-items: center;
}

.arrow-icon {
  color: #000000 !important;
  transition: transform 0.2s ease;
}

.prixTTCContainer {
  display: flex;
  padding: 6px 12px;
  background-color: #F4F4F4;
  color: var(--main-color);
  height: 100%;
  border: solid 1px #F4F4F4;
  flex-direction: row;
  align-content: center;
  justify-content: center;
  align-items: center;
}

.prixTTC {
  color: var(--main-color);
}

.was-validated .form-control:invalid, .form-control.is-invalid {
  border-color: #dc3545;
  padding-right: 0 !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}

.grey-text {
  color: #AAAAAA;
}

.information-rap {
  color: #009DFF;
}

.information-factures {
  color: #009DFF;
}

/* Badge de statut moderne avec radius */
.status-badge {
  display: inline-block;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 500;
  text-align: center;
  border-radius: 1000px;
  min-width: 120px;
  transition: all 0.2s ease;
  white-space: nowrap;
}
/* Payé - Vert clair avec texte vert foncé */
.status-paye {
  background-color: #317B221A;
  color: #317B22;
}

/* À payer - Rouge clair avec texte rouge foncé */
.status-a-payer {
  background-color: #9E04301A;
  color: #9E0430;
}

/* Reste à payer - Orange clair avec texte orange foncé */
.status-reste-a-payer {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

/* En attente - Bleu clair avec texte bleu foncé */
.status-en-attente {
  background-color: #009DFF1A;
  color: #009DFF;
}

/* Par défaut - Gris clair */
.status-default {
  background-color: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
}

/* Effet hover pour tous les badges */
.status-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Badge de statut petit pour les factures individuelles */
.status-badge-small {
  display: inline-block;
  padding: 3px 8px;
  font-size: 0.75rem;
  font-weight: 500;
  text-align: center;
  border-radius: 1000px;
  min-width: 60px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

/* Numéro de devis en grisé */
.devis-number-grey {
  color: #6c757d;
  font-size: 0.8rem;
  margin-top: 2px;
}

/* Informations de facture en grisé */
.facture-info-grey {
  color: #6c757d;
  font-size: 0.8rem;
  margin-top: 4px;
}

.facture-info-grey div {
  margin-bottom: 1px;
}

.details-text{
  color: #AAAAAA;
}

.details-section{
  display: flex;
  justify-content: space-between;
}
</style>



// WEBPACK FOOTER //
// src/views/operationBilan/components/OperationHonoraireComponent.vue